/**
 * Space Activity DB helpers
 *
 * Client-side access utilities for recent Space activity.
 *
 * Primary responsibilities:
 * - Read Space activity through authenticated app routes
 * - Keep activity payloads typed for Space surfaces
 *
 * Dependencies:
 * - Space activity API route
 */

import { authenticatedFetch } from "@/lib/auth/authenticated-fetch"

export interface SpaceActivity {
  id: string
  space_id: string
  user_id: string
  action:
    | "created"
    | "updated"
    | "deleted"
    | "moved"
    | "assigned"
    | "completed"
    | "commented"
    | "shared"
    | "archived"
    | "restored"
  entity_type:
    | "space"
    | "task"
    | "note"
    | "sprint"
    | "event"
    | "whiteboard"
    | "member"
    | "item"
    | "comment"
  entity_id: string
  details: unknown
  created_at: string
}

export interface SpaceActivityActorSummary {
  type: "user" | "losi" | "crew" | "mcp" | "automation" | "agent"
  label: string
  badge?: string
  sourceTool?: string
  mcpServerName?: string
}

export interface SpaceActivityWithUser extends SpaceActivity {
  user?: {
    display_name?: string
    email?: string
    image_url?: string
  }
  actor?: SpaceActivityActorSummary
}

export type SpaceActivityQueryOptions = {
  entityId?: string
  entityType?: SpaceActivity["entity_type"]
}

const readSpaceActivityError = async (
  response: Response,
  fallback: string
) => {
  const responseText = await response.text()

  if (!responseText) {
    return fallback
  }

  try {
    const payload = JSON.parse(responseText) as unknown

    if (
      typeof payload === "object" &&
      payload !== null &&
      "error" in payload &&
      typeof payload.error === "string"
    ) {
      return payload.error
    }
  } catch {
    return responseText
  }

  return responseText
}

export const getSpaceActivities = async (
  spaceId: string,
  limit: number = 10,
  options: SpaceActivityQueryOptions = {}
): Promise<SpaceActivityWithUser[]> => {
  const params = new URLSearchParams()
  params.set("limit", String(limit))

  if (options.entityType) {
    params.set("entity_type", options.entityType)
  }

  if (options.entityId) {
    params.set("entity_id", options.entityId)
  }

  const response = await authenticatedFetch(
    `/api/spaces/${spaceId}/activities?${params.toString()}`,
    {
      cache: "no-store"
    }
  )

  if (!response.ok) {
    throw new Error(
      await readSpaceActivityError(response, "Failed to load activities")
    )
  }

  return (await response.json()) as SpaceActivityWithUser[]
}
